import React, { useState } from "react";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calculator, Wallet, Info, TrendingDown } from "lucide-react";

export const SWPCalculatorPage = () => {
  const [initialAmount, setInitialAmount] = useState(5000000);
  const [monthlyWithdrawal, setMonthlyWithdrawal] = useState(30000);
  const [years, setYears] = useState(20);
  const [expectedReturn, setExpectedReturn] = useState(8);
  const [result, setResult] = useState(null);

  const calculateSWP = () => {
    const P = initialAmount;
    const W = monthlyWithdrawal;
    const r = expectedReturn / 100 / 12;
    const n = years * 12;
    
    let balance = P;
    let totalWithdrawn = 0;
    let monthsLasted = 0;
    
    for (let i = 0; i < n && balance > 0; i++) {
      balance = balance * (1 + r) - W;
      totalWithdrawn += Math.min(W, balance + W);
      monthsLasted = i + 1;
      if (balance <= 0) break;
    }
    
    const finalBalance = Math.max(0, Math.round(balance));
    
    setResult({
      initialInvestment: P,
      monthlyWithdrawal: W,
      totalWithdrawn: Math.round(totalWithdrawn),
      finalBalance,
      yearsLasted: Math.floor(monthsLasted / 12),
      monthsLasted: monthsLasted % 12,
      willLastFullTerm: balance > 0
    });
  };

  const formatCurrency = (num) => {
    if (num >= 10000000) return `₹${(num / 10000000).toFixed(2)} Cr`;
    if (num >= 100000) return `₹${(num / 100000).toFixed(2)} L`;
    return `₹${num.toLocaleString('en-IN')}`;
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main className="pt-32 pb-20">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="text-center mb-12">
            <h1 className="font-serif text-3xl sm:text-4xl font-bold mb-4">
              SWP <span className="gold-text">Calculator</span>
            </h1>
            <p className="text-muted-foreground">
              Plan your systematic withdrawals and ensure your corpus lasts
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Wallet className="w-5 h-5 text-primary" />
                  Withdrawal Plan
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label>Initial Investment</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">₹</span>
                    <Input
                      type="number"
                      value={initialAmount}
                      onChange={(e) => setInitialAmount(Number(e.target.value))}
                      className="pl-8"
                      data-testid="swp-initial-input"
                    />
                  </div>
                  <input
                    type="range"
                    min="100000"
                    max="50000000"
                    step="100000"
                    value={initialAmount}
                    onChange={(e) => setInitialAmount(Number(e.target.value))}
                    className="w-full accent-primary"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Monthly Withdrawal</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">₹</span>
                    <Input
                      type="number"
                      value={monthlyWithdrawal}
                      onChange={(e) => setMonthlyWithdrawal(Number(e.target.value))}
                      className="pl-8"
                      data-testid="swp-withdrawal-input"
                    />
                  </div>
                  <input
                    type="range"
                    min="5000"
                    max="500000"
                    step="5000"
                    value={monthlyWithdrawal}
                    onChange={(e) => setMonthlyWithdrawal(Number(e.target.value))}
                    className="w-full accent-primary"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Withdrawal Period (Years)</Label>
                  <Input
                    type="number"
                    value={years}
                    onChange={(e) => setYears(Number(e.target.value))}
                    min="1"
                    max="40"
                    data-testid="swp-years-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Expected Annual Return (%)</Label>
                  <Input
                    type="number"
                    value={expectedReturn}
                    onChange={(e) => setExpectedReturn(Number(e.target.value))}
                    min="1"
                    max="15"
                    step="0.5"
                    data-testid="swp-return-input"
                  />
                </div>

                <Button onClick={calculateSWP} className="w-full" data-testid="calculate-swp-btn">
                  <Calculator className="w-4 h-4 mr-2" />
                  Calculate SWP
                </Button>
              </CardContent>
            </Card>

            <div className="space-y-6">
              {result && (
                <Card className="bg-card border-border">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <TrendingDown className="w-5 h-5 text-amber-500" />
                      Withdrawal Analysis
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-4 rounded-lg bg-secondary">
                        <p className="text-sm text-muted-foreground">Initial Corpus</p>
                        <p className="text-xl font-bold">{formatCurrency(result.initialInvestment)}</p>
                      </div>
                      <div className="p-4 rounded-lg bg-amber-500/10">
                        <p className="text-sm text-muted-foreground">Monthly Withdrawal</p>
                        <p className="text-xl font-bold text-amber-500">{formatCurrency(result.monthlyWithdrawal)}</p>
                      </div>
                    </div>
                    
                    <div className="p-6 rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 border border-primary/20">
                      <p className="text-sm text-muted-foreground mb-1">Total Withdrawn</p>
                      <p className="text-3xl font-bold gold-text">{formatCurrency(result.totalWithdrawn)}</p>
                      {result.willLastFullTerm ? (
                        <p className="text-sm text-green-500 mt-2">
                          ✓ Your corpus will last the full {years} years
                        </p>
                      ) : (
                        <p className="text-sm text-red-500 mt-2">
                          ⚠ Corpus exhausted in {result.yearsLasted} years {result.monthsLasted} months
                        </p>
                      )}
                    </div>

                    <div className="p-4 rounded-lg bg-secondary">
                      <p className="text-sm text-muted-foreground">Remaining Balance</p>
                      <p className="text-xl font-bold text-green-500">{formatCurrency(result.finalBalance)}</p>
                    </div>
                  </CardContent>
                </Card>
              )}

              <Card className="bg-card border-border">
                <CardContent className="pt-6">
                  <div className="flex gap-3">
                    <Info className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                    <div className="text-sm text-muted-foreground space-y-2">
                      <p><strong>SWP:</strong> Systematic Withdrawal Plan provides regular income from your investments.</p>
                      <p><strong>Tax Efficient:</strong> Only gains portion is taxed, not the full withdrawal.</p>
                      <p><strong>Tip:</strong> Keep withdrawal rate below 4-5% annually for corpus longevity.</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};
